/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */
import {
    Box,
    Divider,
    Flex,
    Heading,
    HStack,
    Image,
    Link,
    Text,
    Tooltip,
    useToast,
} from '@chakra-ui/react'
import kalvium from '@kalvi-education/kalvium-sdk'
import axios from 'axios'
import KC from '@kalvi-education/kalvium-sdk/src/keycloak2'
import { Formik, useField, useFormikContext } from 'formik'
import { PinInputControl } from 'formik-chakra-ui'
import { useContext, useEffect, useReducer, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BROWSER_ENROLLMENT_STATES } from '@/enums/browserEnrollment'

import validationNotStarted from '@/assets/svgIcons/validations-empty.svg'
import validationSuccess from '@/assets/svgIcons/validations-green.svg'
import validationFailed from '@/assets/svgIcons/validations-red.svg'
import { defaultBorder, thickBoxShadow, borderVariant11 } from '../../../../chakraTheme'
import ProctoringPreview from '@/common/ProctoringPreview'
import Toast from '@/common/Toast'
import {
    ArrowForward,
    Info,
    PreviousArrow,
    Tick,
} from '@/components/Icons/default'
import PrimaryButton from '@/components/PrimaryButton'
import { AuthContext } from '@/contexts/AuthContext'
import AxInstructionsContext from '@/contexts/AxInstructionsContext'
import ASSESSMENT_TYPE from '@/enums/assessmentTypes'
import AX_INSTRUCTIONS_ACTION from '@/enums/axInstructionsActions'
import AX_INSTRUCTIONS_STATUS from '@/enums/axInstructionsStatus'
import BROWSERS from '@/enums/browsers'
import PROCTORING_TYPE from '@/enums/proctoringType'
import useBatteryStatus, { LOW_BATTERY_THRESHOLD_PCT } from '@/hooks/useBatteryStatus/useBatteryStatus'
import useBrowserEnrollment from '@/hooks/useBrowserEnrollment/useBrowserEnrollment'
import useProctoring from '@/hooks/useProctoring/useProctoring.js'
import { saveEventLog } from '@/pages/Attempt/_service'
import { getHardwareErrorMessage } from '@/utils/proctoring'
import PROCTORING_ERRORS from '@/enums/proctoringErrors'
import proctoringGlitchRefresh from '@/utils/proctoringGlitchRefresh'
import { Camcorder, ScreenRecorder } from '../../components/Icons'
import useFullScreen from '@/hooks/useFullScreen/useFullScreen'
import { HugeiconsIcon } from '@hugeicons/react'
import { Alert01FreeIcons, Alert02FreeIcons } from '@hugeicons/core-free-icons'
import FULL_SCREEN_EXIT_STATES from '@/enums/fullScreenExitStates'
import { isProd } from '@/utils/common'
import { readBrowserEnrollmentResumeSnapshot } from '@/utils/sharedAttemptStorage'
import { REGULAR_ASSESSMENT_SOURCE_FLOW } from '@/utils/regularAssessmentStorage'

const OtpSection = ({
    // isOtpValid,
    setIsOtpValid,
}) => {
    const {
        // store,
        dispatch,
    } = useContext(AxInstructionsContext)
    const { isValid } = useFormikContext()
    const [
        field,
        // meta
    ] = useField('otp')

    const saveOtpToStore = otp => {
        dispatch({
            type: AX_INSTRUCTIONS_ACTION.UPDATE_OTP,
            otp: otp,
        })
    }

    return (
        <Box>
            <Text color={'#5c5c5c'}>
                This test requires a security code to proceed. Please reach out
                to your mentor/invigilator for the code.
            </Text>
            <Box pt={'4'}>
                <HStack gap={6}>
                    <PinInputControl
                        isInvalid={!isValid}
                        pinAmount={6}
                        name={'otp'}
                        isRequired
                        onBlur={field.onBlur}
                        pinInputProps={{
                            isInvalid: !isValid,
                            variant: 'retro',
                            placeholder: '',
                            onComplete: value => {
                                saveOtpToStore(value)
                                setIsOtpValid(true)
                            },
                        }}
                    />
                </HStack>
            </Box>
        </Box>
    )
}

const NavFooter = ({
    nextBtnRef,
    isAvStreamAvailable,
    isScreenCaptureAvailable,
    isEnrollmentSetupOk,
    isProctored,
    isChrome,
    isFullScreenEnabled = false,
    isBatteryBlocked = false,
    cacheAttemptStorage,
    getAttemptStorage,
    attemptSourceFlow,
}) => {
    const { values, setFieldError } = useFormikContext()
    const { store, dispatch } = useContext(AxInstructionsContext)
    const { getBrowserName, getBrowserVersion, getOS, getOSVersion } = useProctoring()

    const pathParams = useParams()
    const toast = useToast()
    const navigate = useNavigate()

    const {
        openFullScreen,
        isFullScreen
    } = useFullScreen()

    const isExistingAttempts = store?.attempts?.length && !store.retryEnabled
    const isPreview = store?.isPreview === true
    const [isNextBtnLoading, setIsNextBtnLoading] = useState(false)

    const browserDetails = `Browser: ${getBrowserName()} ${getBrowserVersion()}`
    const osDetails = `OS: ${getOS()} ${getOSVersion()}`
    const systemInfo = `${browserDetails}, ${osDetails}`

    const isNextDisabled =
        (store?.assessment?.is_key_required &&
            !(values.otp.length === 6) &&
            !isExistingAttempts) ||
        (isProctored && !isAvStreamAvailable) ||
        (isProctored && !isScreenCaptureAvailable) ||
        (isFullScreenEnabled && !isFullScreen) ||
        !isEnrollmentSetupOk ||
        !isChrome ||
        isBatteryBlocked

    // First concrete blocker for the Begin/Resume button, surfaced as a
    // tooltip. Pure loading states are intentionally left out — the button
    // spinner already conveys those.
    const disabledReason = (() => {
        if (!isChrome)
            return 'Please use Google Chrome to take this assessment.'
        if (
            store?.assessment?.is_key_required &&
            !(values.otp.length === 6) &&
            !isExistingAttempts
        )
            return 'Please enter a valid security code to proceed.'
        if (isProctored && !isAvStreamAvailable)
            return 'Please allow camera and microphone access to continue.'
        if (isProctored && !isScreenCaptureAvailable)
            return 'Please share your entire screen to continue.'
        if (isFullScreenEnabled && !isFullScreen)
            return 'Please switch to full screen to continue.'
        if (isBatteryBlocked)
            return (
                'Battery is low. Please connect your device to a power source before continuing.'
            )
        if (!isEnrollmentSetupOk)
            return 'Please complete the browser security setup above to continue.'
        return ''
    })()

    const isOpenAssessment = store?.assessment.visibility === 'open'

    const handlePrevious = () => {
        dispatch({ type: AX_INSTRUCTIONS_ACTION.PREV_STEP })
    }

    const markStepAsComplete = () => {
        // prevent irrelevant dispatch
        if (
            store?.currentStep &&
            store.steps &&
            store.steps[store.currentStep].status ===
            AX_INSTRUCTIONS_STATUS.COMPLETED
        )
            return
        dispatch({
            type: AX_INSTRUCTIONS_ACTION.UPDATE_CURRENT_STEP_STATUS,
            status: AX_INSTRUCTIONS_STATUS.COMPLETED,
        })
    }

    const handleNext = async (isAutoSubmitOnGoingAttempt = false) => {
        // create an attempt
       const isScreenExtend =  window.screen.isExtended
        if (isScreenExtend && store?.assessment?.disable_high_integrity_checks !== true && !isAutoSubmitOnGoingAttempt && isProd()) {
            toast({
                title: "External Screen Detected. Please disconnect",
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            })
            return;
        }
        try {
            setIsNextBtnLoading(true)
            // Glitch refresh = connection issue, not a violation: tell the
            // backend to resume without deducting a life. (Auto-submit resumes
            // never deduct anyway.)
            const isGlitchResume =
                isExistingAttempts &&
                !isAutoSubmitOnGoingAttempt &&
                proctoringGlitchRefresh.peek(pathParams.ax_slug)
            if (isGlitchResume) proctoringGlitchRefresh.clear()
            const extras =
                !isExistingAttempts && store?.assessment?.allow_extra
                    ? store.formValues
                    : isGlitchResume
                    ? { resume_reason: 'proctoring_glitch' }
                    : undefined

            const data = {
                slug: pathParams.ax_slug,
                otp:
                    isExistingAttempts ||
                    !store?.assessment?.is_key_required
                        ? undefined
                        : values.otp,
                // resume_reason/is_preview ride in extras (SDK forwards extras as-is).
                extras: isPreview ? { ...extras, is_preview: true } : extras,
            }
            let attemptData

            if (isOpenAssessment && isExistingAttempts) {
                const { attemptId, token } = getAttemptStorage
                    ? getAttemptStorage(pathParams.ax_slug, store?.attempts?.[0])
                    : {
                          attemptId: localStorage.getItem(
                              `attempt_id_${pathParams.ax_slug}`
                          ),
                          token: localStorage.getItem(
                              `attempt_token_${pathParams.ax_slug}`
                          ),
                      }
                attemptData = await saveEventLog({
                    attemptId,
                    isTryout: true,
                    token,
                    events: [],
                })
            } else {
                attemptData = isOpenAssessment
                    ? await kalvium.services.assessment.createOpenAxAttempt({
                        ...data,
                        user_id: KC.getParsedToken().sub,
                    })
                    : await kalvium.services.assessment.createAxAttempt(data)

                if (cacheAttemptStorage) {
                    cacheAttemptStorage(pathParams.ax_slug, attemptData)
                } else {
                    localStorage.setItem(
                        `attempt_id_${pathParams.ax_slug}`,
                        attemptData.id
                    )
                    localStorage.setItem(
                        `attempt_token_${pathParams.ax_slug}`,
                        attemptData.token
                    )
                }
            }


            if (attemptData?.id && !isAutoSubmitOnGoingAttempt) {
                try {
                    await saveEventLog({
                        attemptId: attemptData.id,
                        ...(isOpenAssessment && { token: attemptData.token }),
                        events: [{
                            event: isExistingAttempts ? 'resume' : 'started',
                            extras: {
                                content: systemInfo,
                                user_ts: Math.round(new Date().getTime() / 1000),
                            },
                        }],
                    })
                } catch (error) {
                    // Suppress the error silently
                    console.debug('Failed to save event log:', error)
                }
            }
            
            // For auto-submit, the Attempt page clears 'exited' only after
            // the submit API actually succeeds.
            if (!isAutoSubmitOnGoingAttempt) {
                localStorage.removeItem("exited")
            }

            const stateData = isOpenAssessment
                ? {
                    assessmentType: ASSESSMENT_TYPE.TRYOUT,
                    token: attemptData.token,
                    attemptData: attemptData,
                }
                : { attemptData }

            markStepAsComplete()

            if (attemptData.id) {
                if (attemptSourceFlow === REGULAR_ASSESSMENT_SOURCE_FLOW) {
                    localStorage.setItem('prevPage', `/assessment/${pathParams.ax_slug}`)
                }
                // go to attempt page
                navigate(
                    `/assessment/${pathParams.ax_slug}/attempt/${attemptData.id}`,
                     { 
                        replace: isAutoSubmitOnGoingAttempt,
                        state: {
                            ...stateData,
                            isAutoSubmitOnGoingAttempt,
                            ...(attemptSourceFlow && {
                                sourceFlow: attemptSourceFlow,
                            }),
                        }
                    }
                )
            }

            setIsNextBtnLoading(false)
        } catch (error) {
            setIsNextBtnLoading(false)
            if (error.message === 'OTP verification failed') {
                // show this error in the pin input
                setFieldError(
                    'otp',
                    'The code you’ve entered is incorrect. Please try again.'
                )
                return
            }

            toast({
                title: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            })
        }
    }


    useEffect(() => {
        if(store?.fullScreenExitState === FULL_SCREEN_EXIT_STATES.AUTO_SUBMIT_ATTEMPT){
            handleNext(true);
        }
    },[store?.fullScreenExitState])

    return (
        <Flex
            w={'full'}
            my={'6'}
            justifyContent={'space-between'}
            flexDirection={'row-reverse'}
        >
            <Tooltip
                isDisabled={
                    !isNextDisabled || isNextBtnLoading || !disabledReason
                }
                hasArrow
                label={
                    <Box fontWeight={'normal'}>
                        <HStack>
                            <Box mr={5}>
                                <Info h={6} w={6} />
                            </Box>
                            <Box marginInlineStart={'0 !important'}>
                                {disabledReason}
                            </Box>
                        </HStack>
                    </Box>
                }
                placement="top-end"
                bg={'ziggurat'}
                color={'codgray'}
                padding={'6'}
                boxShadow={thickBoxShadow}
                border={defaultBorder}
                borderRadius={'none'}
                offset={[-4, 10]}
                sx={{
                    '.chakra-tooltip__arrow': {
                        boxShadow: '8px 2px 0 #333333 !important',
                    },
                }}
            >
                {/* full screen mode */}
                <Flex>
                    <PrimaryButton
                        wrapperProps={{
                            marginLeft: 'auto',
                            _hover: {},
                        }}
                        onClick={e => {
                            if (isNextDisabled) return e.preventDefault()
                            if(store?.assessment?.full_screen_mode){
                              openFullScreen()
                            }
                            handleNext()
                        }}
                        variant={isNextDisabled ? 'disabled' : 'clickable'}
                        rightIcon={
                            <ArrowForward
                                fontSize={'2xl'}
                                color={isNextDisabled ? 'alto2' : 'white'}
                            />
                        }
                        ref={nextBtnRef}
                        isLoading={isNextBtnLoading}
                        colorScheme={'rum'}
                        my={{ base: 4, md: 0 }}
                        w={{ base: 'full', md: 'auto' }}
                        _hover={{}}
                    >
                        {isExistingAttempts
                            ? 'Resume Assessment'
                            : 'Begin Assessment'}
                    </PrimaryButton>
                </Flex>
            </Tooltip>

            {/* dont render previous btn if there are existing attempts(user resuming assessment) or only one step is present */}
            {!isExistingAttempts && store?.steps?.length > 1 && (
                <Flex
                    align="center"
                    cursor="pointer"
                    onClick={handlePrevious}
                    color="emperor"
                >
                    <PreviousArrow h={6} w={6} />
                    <Heading
                        ml={2}
                        fontSize={{ base: 'sm', md: 'md' }}
                        fontWeight="bold"
                    >
                        Previous
                    </Heading>
                </Flex>
            )}
        </Flex>
    )
}

const ProctoringSetup = ({
    assessment,
    autoSubmitWarningMessage,
    cacheAttemptStorage,
    getAttemptStorage,
    attemptSourceFlow,
}) => {
    const { kalviumKc } = useContext(AuthContext)
    const { store } = useContext(AxInstructionsContext)
    const { ax_slug: assessmentSlug } = useParams()
    const requiresSquadChecks = assessment?.visibility !== 'open'
    const remLives=store?.attempts?.find(attempt => !attempt.closed && attempt.full_screen_mode)?.remaining_lives || 0

    const isDisplayWarningMessage = store?.fullScreenExitState === FULL_SCREEN_EXIT_STATES.PROCTORING_SETUP_STEP && assessment?.full_screen_integrity_mode

    const openAttempt = store?.attempts?.find(attempt => !attempt.closed)
    const resumeSnapshot = readBrowserEnrollmentResumeSnapshot({
        attemptId: openAttempt?.id,
        assessmentSlug,
    })

    const {
        setTemporaryEid,
        isTemporaryEidAccepted,
        fetchEnrollmentStatus,
        isPermanentEidAccepted: isHibuEnrollmentAccepted,
        temporaryEid,
        squadEnforcedMode,
        extensionStatus,
        temporaryLookup,
        isMismatched,
        isSnoozed,
    } = useBrowserEnrollment()

    const {
        openFullScreen,
        isFullScreen
    } = useFullScreen()

    const { getBrowserName, getUserAgentData } = useProctoring()

    const [isEnrollmentSetupOk, setIsEnrollmentSetupOk] = useState(false)
    const [enrollmentStatus, setEnrollmentStatus] = useState(null)
    const battery = useBatteryStatus()
    const isBatteryBlocked =
        battery.supported &&
        battery.levelPct != null &&
        battery.levelPct < LOW_BATTERY_THRESHOLD_PCT &&
        !battery.charging

    const isChrome =
        import.meta.env.VITE_ASSESSMENT_CHROME_CHECK === 'false' ||
        assessment?.disable_high_integrity_checks === true ||
        (getBrowserName() === BROWSERS.CHROME &&
            getUserAgentData()?.find(b => b.brand === 'Google Chrome'))

    const isPermanentEidAccepted =
        Boolean(resumeSnapshot) || isHibuEnrollmentAccepted
    const hasResumeEnrollment = Boolean(resumeSnapshot)
    const lastEnrollmentDiagnostic = useRef('')
    useEffect(() => {
        const warningShown = requiresSquadChecks && squadEnforcedMode.status === 'known' &&
            squadEnforcedMode.value === 'enforced' &&
            assessment?.disable_high_integrity_checks !== true &&
            !isHibuEnrollmentAccepted && !hasResumeEnrollment &&
            !isTemporaryEidAccepted && !temporaryEid
        const diagnostic = {
            assessmentSlug,
            warningShown,
            hibuAccepted: isHibuEnrollmentAccepted,
            resumeAccepted: hasResumeEnrollment,
            temporaryAccepted: isTemporaryEidAccepted,
            hasTemporaryEid: Boolean(temporaryEid),
            extensionStatus,
            temporaryLookup,
            isMismatched,
            isSnoozed,
            squadPolicyStatus: squadEnforcedMode.status,
            squadPolicyValue: squadEnforcedMode.value,
            integrityChecksDisabled: assessment?.disable_high_integrity_checks === true,
        }
        const snapshot = JSON.stringify(diagnostic)
        if (lastEnrollmentDiagnostic.current === snapshot) return
        lastEnrollmentDiagnostic.current = snapshot
        const endpoint = import.meta.env.VITE_BOA_ENDPOINT
        if (!endpoint) return
        let cancelled = false
        let retryTimer
        const send = async (retry = true) => {
            try {
                await axios.post(
                    `${endpoint.replace(/\/$/, '')}/misc/browser-enrollment/diagnostics`,
                    diagnostic,
                    { headers: { Authorization: `Bearer ${KC.getToken()}` }, timeout: 5000 }
                )
            } catch (error) {
                // Retry once for network/server failure, never change assessment state.
                if (!cancelled && retry && (!error?.response || error.response.status >= 500)) {
                    retryTimer = setTimeout(() => { void send(false) }, 1500)
                }
            }
        }
        void send()
        return () => {
            cancelled = true
            clearTimeout(retryTimer)
            // Allow StrictMode's replacement effect to send after cleanup.
            if (lastEnrollmentDiagnostic.current === snapshot) lastEnrollmentDiagnostic.current = ''
        }
    }, [assessmentSlug, squadEnforcedMode, requiresSquadChecks, assessment?.disable_high_integrity_checks,
        isHibuEnrollmentAccepted, hasResumeEnrollment, isTemporaryEidAccepted,
        temporaryEid, extensionStatus, temporaryLookup, isMismatched, isSnoozed])
    useEffect(() => {
        const isSquadEnforced =
            squadEnforcedMode.status === 'known' &&
            squadEnforcedMode.value === 'enforced'

        const isIntegrityCheckEnabled =
            requiresSquadChecks && assessment?.disable_high_integrity_checks !== true

        const isEnrollmentAccepted =
            isPermanentEidAccepted || isTemporaryEidAccepted

        if (!isIntegrityCheckEnabled) {
            setIsEnrollmentSetupOk(true)
        } else if (squadEnforcedMode.status !== 'known') {
            setIsEnrollmentSetupOk(false)
        } else {
            setIsEnrollmentSetupOk(!isSquadEnforced || isEnrollmentAccepted)
        }
    }, [
        squadEnforcedMode,
        requiresSquadChecks,
        isPermanentEidAccepted,
        isTemporaryEidAccepted,
        assessment?.disable_high_integrity_checks,
    ])
    const {
        isVideoCaptureAvailable,
        isAudioCaptureAvailable,
        isScreenCaptureAvailable,
        startAVCapture,
        startScreenCapture,
        avStream,
        screenStream,
        attachOnAvError,
        attachOnScreenError,
    } = useProctoring()
    const isAvStreamAvailable =
        isVideoCaptureAvailable && isAudioCaptureAvailable
    const nextBtnRef = useRef(null)
    const toast = useToast()

    const onAvError = async error => {
        const errorMessage = await getHardwareErrorMessage(error)
        toast({
            title: errorMessage,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
            render: props => <Toast {...props} />,
        })
    }
    const onScreenError = error => {
        let errorMessage
        switch (error.message) {
            case PROCTORING_ERRORS.PERMISSION_DENIED:
                errorMessage = 'Please enable permissions to share your screen'
                break
            case PROCTORING_ERRORS.PERMISSION_DENIED_BY_SYSTEM:
                errorMessage =
                    'The system denied permission to access your Screen. Please check your Privacy & Security settings and try again.'
                break
            default:
                errorMessage = error.message
                console.error(error)
        }
        toast({
            title: errorMessage,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
            render: props => <Toast {...props} />,
        })
    }

    useEffect(() => {
        attachOnAvError(onAvError)
        attachOnScreenError(onScreenError)

        return () => {
            attachOnAvError(undefined)
            attachOnScreenError(undefined)
        }
    }, [])

    const isExistingAttempts = store?.attempts?.length && !store.retryEnabled

    const _handleExceptionRequest = async () => {
        const temporaryEid = localStorage.getItem('RB_ENROLLMENT_ID')
        if (!temporaryEid) {
            let enrollment
            try {
                enrollment =
                    await kalvium.services.browserEnrollment.createEnrollment(
                        kalviumKc?.getParsedToken()?.email,
                        import.meta.env.VITE_REALM
                    )
            } catch (err) {
                console.error('unable to create enrollment')
                console.error(err)
            }
            const enrollmentId = enrollment?.data?.id
            localStorage.setItem('RB_ENROLLMENT_ID', enrollmentId)
            setTemporaryEid(enrollmentId)
            setEnrollmentStatus(enrollment?.data?.state)
        }
    }

    const [isOtpValid, setIsOtpValid] = useState(false)

    return (
        <Formik
            initialValues={{
                otp: store?.otp || '',
            }}
            validateOnMount={true}
            validateOnChange={true}
            validate={values => {
                /**
                 * error for pin input is only when backend says that the otp is incorrect
                 * otherwise the pin input is error free
                 * using formik functionality to mimic a success state which is :
                 *      when the input has all 6 digits entered
                 */
                if (values.otp.length === 6) {
                    setIsOtpValid(true)
                } else {
                    setIsOtpValid(false)
                }
            }}
        >
            {({
                isValid,
                // touched
            }) => (
                <Flex
                    pl={'16'}
                    pr={16}
                    pt={'96px'}
                    flexDirection="column"
                    height={'full'}
                >
                    <Text
                        fontFamily={'Rubik'}
                        fontWeight={'700'}
                        fontSize={'24px'}
                    >
                        Setup Your Test Environment
                    </Text>
                    <Text fontSize={'14px'} mt={'4'}>
                        To proceed, please ensure you pass all the following
                        checks.
                    </Text>

                    <Flex
                        gap={'8'}
                        flexDirection={'column'}
                        mt={'12'}
                        flexGrow={1}
                    >
                        {requiresSquadChecks && squadEnforcedMode.status === 'known' &&
                            squadEnforcedMode.value === 'enforced' &&
                            assessment?.disable_high_integrity_checks !==
                            true && (
                                <Flex
                                    alignItems={'flex-start'}
                                    gap={'4'}
                                    width="100%"
                                >
                                    {isPermanentEidAccepted ||
                                        isTemporaryEidAccepted === true ? (
                                        <Image
                                            src={validationSuccess}
                                            pt={'1'}
                                        />
                                    ) : (
                                        <Image
                                            src={validationFailed}
                                            pt={'1'}
                                        />
                                    )}
                                    <Flex flexDirection={'column'} gap={'2'}>
                                        <Text
                                            fontWeight={'700'}
                                            fontSize={'16px'}
                                            fontFamily={'Rubik'}
                                            margin={'0'}
                                        >
                                            Browser Compatibility
                                        </Text>

                                        {isPermanentEidAccepted ||
                                            isTemporaryEidAccepted === true ? (
                                            <Text>
                                                Your browser is recognized to
                                                take this assessment.
                                            </Text>
                                        ) : !temporaryEid ? (
                                            <Text>
                                                This assessment needs to be
                                                taken in a recognized browser.
                                                Please use a recognized browser
                                                or{' '}
                                                <Link
                                                    onClick={
                                                        _handleExceptionRequest
                                                    }
                                                    textDecoration="underline"
                                                    _hover={{
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    request an exception
                                                </Link>
                                                .
                                            </Text>
                                        ) : (
                                            <Text>
                                                We&apos;ve received your
                                                exception request. Please wait
                                                while your request is being
                                                validated.{' '}
                                                {enrollmentStatus ===
                                                    BROWSER_ENROLLMENT_STATES.PENDING && (
                                                        <Link
                                                            onClick={
                                                                fetchEnrollmentStatus
                                                            }
                                                            textDecoration="underline"
                                                            _hover={{
                                                                cursor: 'pointer',
                                                                color: 'blue.500',
                                                            }}
                                                        >
                                                            Refresh
                                                        </Link>
                                                    )}
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                            )}
                        {import.meta.env.VITE_ASSESSMENT_CHROME_CHECK ===
                            'true' &&
                            assessment?.disable_high_integrity_checks !==
                            true && (
                                <Flex alignItems={'flex-start'} gap={'4'}>
                                    {isChrome ? (
                                        <Image
                                            src={validationSuccess}
                                            pt={'1'}
                                        />
                                    ) : (
                                        <Image
                                            src={validationFailed}
                                            pt={'1'}
                                        />
                                    )}
                                    <Flex flexDirection={'column'} gap={'2'}>
                                        <Text
                                            fontWeight={'700'}
                                            fontSize={'16px'}
                                            fontFamily={'Rubik'}
                                        >
                                            Browser Check
                                        </Text>
                                        {isChrome ? (
                                            <Text>
                                                You are all set! You&apos;re
                                                using Google Chrome.
                                            </Text>
                                        ) : (
                                            <Text>
                                                This assessment requires you to
                                                use Google Chrome Browser.
                                                Please switch to Google Chrome
                                                to proceed.
                                            </Text>
                                        )}
                                    </Flex>
                                </Flex>
                            )}
                        {assessment.proctoring === 'remote_proctoring' && (
                            <Flex alignItems={'flex-start'} gap={'4'}>
                                {isVideoCaptureAvailable &&
                                    isAudioCaptureAvailable &&
                                    isScreenCaptureAvailable ? (
                                    <Image src={validationSuccess} pt={'1'} />
                                ) : (
                                    <Image src={validationFailed} pt={'1'} />
                                )}
                                <Flex flexDirection={'column'} gap={'2'}>
                                    <Text
                                        fontWeight={'700'}
                                        fontSize={'16px'}
                                        fontFamily={'Rubik'}
                                    >
                                        Enable access to your Camera, Microphone
                                        and Screen Sharing
                                    </Text>
                                    <Flex flexGrow={1}>
                                        <Flex flexDirection={'column'}>
                                            <ProctoringPreview
                                                icon={Camcorder}
                                                title="Camera & Microphone"
                                                stream={avStream}
                                                mirror={true}
                                            />
                                            {isAvStreamAvailable ? (
                                                <Flex alignItems="center">
                                                    <Tick color="fruitsalad" />
                                                    <Text
                                                        color="fruitsalad"
                                                        ml={1}
                                                    >
                                                        Enabled
                                                    </Text>
                                                </Flex>
                                            ) : (
                                                <Flex>
                                                    <Text
                                                        fontFamily="Rubik"
                                                        fontSize="sm"
                                                        color="tundora"
                                                        as="u"
                                                        onClick={() => {
                                                            startAVCapture()
                                                        }}
                                                        cursor="pointer"
                                                    >
                                                        Enable
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Flex>
                                        <Flex flexDirection={'column'} ml={12}>
                                            <ProctoringPreview
                                                icon={ScreenRecorder}
                                                title="Screen Sharing"
                                                stream={screenStream}
                                            />
                                            {isScreenCaptureAvailable ? (
                                                <Flex alignItems="center">
                                                    <Tick color="fruitsalad" />
                                                    <Text
                                                        color="fruitsalad"
                                                        ml={1}
                                                    >
                                                        Enabled
                                                    </Text>
                                                </Flex>
                                            ) : (
                                                <Flex>
                                                    <Text
                                                        fontFamily="Rubik"
                                                        fontSize="sm"
                                                        color="tundora"
                                                        as="u"
                                                        onClick={() => {
                                                            startScreenCapture()
                                                        }}
                                                        cursor="pointer"
                                                    >
                                                        Enable
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Flex>
                                    </Flex>
                                </Flex>
                            </Flex>
                        )}
                        {assessment.full_screen_mode === true && (
                            <Flex alignItems={'flex-start'} gap={'4'}>
                                {isFullScreen ? (
                                    <Image src={validationSuccess} pt={'1'} />
                                ) : (
                                    <Image src={validationFailed} pt={'1'} />
                                )}
                                <Flex flexDirection={'column'} gap={'2'}>
                                    <Text
                                        fontWeight={'700'}
                                        fontSize={'16px'}
                                        fontFamily={'Rubik'}
                                    >
                                        Enable Full-Screen
                                    </Text>
                                    <div className='text-[#5C5C5C]'>
                                        This test requires to be taken in a full screen. Navigating away from the tab or window during the assessment<br />
                                        may submit your attempt immediately.
                                        {' '}
                                        {
                                            !isFullScreen && (
                                                <button
                                                    onClick={openFullScreen}
                                                    className="underline hover:cursor-pointer text-[#5C5C5C] font-bold"
                                                >
                                                    Go Full Screen
                                                </button>
                                            )
                                        }
                                    </div>
                                    <Flex
                                    >
                                        <Image
                                            src={'/images/bulb.svg'}
                                            htmlWidth={24}
                                            htmlHeight={24}
                                        />

                                        <Text ml={3} fontSize={'sm'} color={'#575757'}>
                                            Pro-Tip: Make sure to turn off all system and
                                            browser notifications before you start!
                                        </Text>
                                    
                                    </Flex>
                                    {isDisplayWarningMessage && !isFullScreen &&(
                                         <div className='flex flex-col justify-center items-start gap-1.5 px-3 py-2.5 border border-amber-300 bg-amber-50 rounded-none'>
                                            <h1 className="text-amber-800 text-sm leading-5 font-normal">You've exited full screen. Please enter full screen mode to continue with your attempt.</h1>
                                            <div className='flex items-center gap-2'>   
                                                <HugeiconsIcon icon={Alert02FreeIcons} size={16} />
                                                <p className='text-amber-800 text-sm leading-5 font-bold'>You've {remLives} {remLives > 1 ? 'warnings' : 'warning'} left</p>
                                            </div>
                                    </div>
                                    )}
                                </Flex>
                            </Flex>
                        )}
                        {store?.assessment?.is_key_required &&
                            !isExistingAttempts ? (
                            <Flex alignItems={'flex-start'} gap={'4'}>
                                {!isValid ? (
                                    <Image src={validationFailed} pt={'1'} />
                                ) : isOtpValid ? (
                                    <Image src={validationSuccess} pt={'1'} />
                                ) : (
                                    <Image
                                        src={validationNotStarted}
                                        pt={'1'}
                                    />
                                )}

                                <Flex flexDirection={'column'} gap={'2'}>
                                    <Text
                                        fontWeight={'700'}
                                        fontSize={'16px'}
                                        fontFamily={'Rubik'}
                                    >
                                        Enter the Security code
                                    </Text>

                                    <Box mb={12}>
                                        <OtpSection
                                            isOtpValid={isOtpValid}
                                            setIsOtpValid={setIsOtpValid}
                                        />
                                    </Box>
                                </Flex>
                            </Flex>
                        ) : null}

                        {battery.supported && (
                            <Flex alignItems={'flex-start'} gap={'4'}>
                                {isBatteryBlocked ? (
                                    <Image src={validationFailed} pt={'1'} />
                                ) : (
                                    <Image src={validationSuccess} pt={'1'} />
                                )}
                                <Flex flexDirection={'column'} gap={'2'}>
                                    <Text
                                        fontWeight={'700'}
                                        fontSize={'16px'}
                                        fontFamily={'Rubik'}
                                    >
                                        Battery
                                    </Text>
                                    {isBatteryBlocked ? (
                                        <Text>
                                            Battery at {battery.levelPct}%.
                                            Please connect your device to a
                                            power source before continuing.
                                        </Text>
                                    ) : battery.charging ? (
                                        <Text>
                                            Battery at {battery.levelPct}% and
                                            charging. You&apos;re good to go.
                                        </Text>
                                    ) : (
                                        <Text>
                                            Battery at {battery.levelPct}%.
                                            You&apos;re good to go, but we
                                            recommend connecting your device to
                                            a power source for an uninterrupted
                                            test-taking experience.
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                        )}
                    </Flex>

                    <Divider
                        borderBottom={'3px solid rgba(69, 69, 69, 0.2);'}
                    />
                    <NavFooter
                        nextBtnRef={nextBtnRef}
                        isAvStreamAvailable={isAvStreamAvailable}
                        isScreenCaptureAvailable={isScreenCaptureAvailable}
                        isEnrollmentSetupOk={isEnrollmentSetupOk}
                        isProctored={
                            assessment.proctoring ===
                            PROCTORING_TYPE.REMOTE_PROCTORING
                        }
                        isFullScreenEnabled={assessment?.full_screen_mode}
                        isChrome={isChrome}
                        isBatteryBlocked={isBatteryBlocked}
                        cacheAttemptStorage={cacheAttemptStorage}
                        getAttemptStorage={getAttemptStorage}
                        attemptSourceFlow={attemptSourceFlow}
                    />
                </Flex>
            )}
        </Formik>
    )
}

export default ProctoringSetup
